// Image crop overlay: shown when the user enters crop mode (Alt/Opt + double-
// click on a selected image). Renders the full original image at reduced
// opacity as a reference, with a draggable / resizable crop rectangle on top.
// The crop rect is stored as 0–1 fractions (cropX/Y/W/H) of the image box so
// it stays stable when the image is moved or resized on the canvas.
//
// Interaction:
//   - Drag inside the crop rect -> move it (clamped to the image bounds).
//   - Drag a handle -> resize from that corner/edge (clamped, can't invert).
//   - Double-click empty canvas (handled by Canvas) -> exit crop mode.
import React, { useRef } from "react";

const HANDLES = [
  { k: "nw", x: 0, y: 0 },
  { k: "n", x: 0.5, y: 0 },
  { k: "ne", x: 1, y: 0 },
  { k: "e", x: 1, y: 0.5 },
  { k: "se", x: 1, y: 1 },
  { k: "s", x: 0.5, y: 1 },
  { k: "sw", x: 0, y: 1 },
  { k: "w", x: 0, y: 0.5 },
];

const clamp01 = (v) => Math.max(0, Math.min(1, v));

export default function ImageCropOverlay({
  element,
  scale = 1,
  getSvgPoint,
  onLiveUpdate,
  onResizeStart,
  onResizeEnd,
}) {
  const dragRef = useRef(null);
  const el = element;
  // Original (uncropped) source image box — the crop rect lives inside this.
  const ix = el.attrs.origX ?? el.attrs.x ?? 0;
  const iy = el.attrs.origY ?? el.attrs.y ?? 0;
  const iw = el.attrs.origW ?? el.attrs.width ?? 0;
  const ih = el.attrs.origH ?? el.attrs.height ?? 0;
  if (iw === 0 || ih === 0) return null;

  // Current crop rect (0–1 fractions of the orig box). cropX/Y is the top-left
  // corner, cropW/H is the width/height (not the right/bottom edge). Defaults
  // to the full image. Clamp so the rect stays within the image bounds.
  const cx0 = clamp01(Number(el.attrs.cropX) || 0);
  const cy0 = clamp01(Number(el.attrs.cropY) || 0);
  const cw0 = clamp01(Math.min(Number(el.attrs.cropW) || 1, 1 - cx0));
  const ch0 = clamp01(Math.min(Number(el.attrs.cropH) || 1, 1 - cy0));

  // Crop rect in user space (absolute coords within the orig box).
  const rx = ix + cx0 * iw;
  const ry = iy + cy0 * ih;
  const rw = cw0 * iw;
  const rh = ch0 * ih;

  const handleSize = 9 / scale;
  const stroke = 1 / scale;

  // Build a darkened mask around the crop rect: four rects covering the area
  // outside the crop within the image bounds. This makes the cropped-away
  // region visibly dimmer while keeping the cropped region crisp.
  const maskRects = [
    // top strip
    { x: ix, y: iy, w: iw, h: ry - iy },
    // bottom strip
    { x: ix, y: ry + rh, w: iw, h: iy + ih - (ry + rh) },
    // left strip (between top & bottom)
    { x: ix, y: ry, w: rx - ix, h: rh },
    // right strip
    { x: rx + rw, y: ry, w: ix + iw - (rx + rw), h: rh },
  ].filter((r) => r.w > 0 && r.h > 0);

  const startDrag = (e, mode, handle) => {
    e.stopPropagation();
    e.preventDefault();
    onResizeStart?.();
    // NOTE: do NOT call setPointerCapture here. It would route subsequent
    // pointermove/up events to the crop-rect element instead of window, but
    // our move/up listeners are on window — capturing would starve them.
    const startPt = getSvgPoint(e.clientX, e.clientY);
    const start = { cx: cx0, cy: cy0, cw: cw0, ch: ch0 };
    dragRef.current = { mode, handle, startPt, start };

    const onMove = (ev) => {
      if (!dragRef.current) return;
      const p = getSvgPoint(ev.clientX, ev.clientY);
      // Delta in image-fraction space (relative to the orig box).
      const dx = (p.x - startPt.x) / iw;
      const dy = (p.y - startPt.y) / ih;

      let { cx, cy, cw, ch } = start;
      if (mode === "move") {
        cx = clamp01(start.cx + dx);
        cy = clamp01(start.cy + dy);
        // Keep the rect inside the image: shift back if it would overflow.
        if (cx + cw > 1) cx = 1 - cw;
        if (cy + ch > 1) cy = 1 - ch;
      } else {
        // Resize: adjust the matching edge, keep the opposite edge fixed.
        if (handle.includes("w")) {
          const newX = clamp01(start.cx + dx);
          cw = start.cx + start.cw - newX;
          if (cw < 0.02) { cw = 0.02; } // enforce a minimum size
          cx = start.cx + start.cw - cw;
        }
        if (handle.includes("e")) {
          cw = Math.max(0.02, start.cw + dx);
          if (cx + cw > 1) cw = 1 - cx;
        }
        if (handle.includes("n")) {
          const newY = clamp01(start.cy + dy);
          ch = start.cy + start.ch - newY;
          if (ch < 0.02) ch = 0.02;
          cy = start.cy + start.ch - ch;
        }
        if (handle.includes("s")) {
          ch = Math.max(0.02, start.ch + dy);
          if (cy + ch > 1) ch = 1 - cy;
        }
      }
      // Update crop fractions AND bake the cropped display box (x/y/width/
      // height) so the selection bbox / Transform panel reflect the crop.
      onLiveUpdate(el.id, {
        ...el.attrs,
        cropX: cx,
        cropY: cy,
        cropW: cw,
        cropH: ch,
        x: ix + cx * iw,
        y: iy + cy * ih,
        width: cw * iw,
        height: ch * ih,
      });
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      onResizeEnd?.();
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const cursorOf = (k) => {
    const map = { n: "ns", s: "ns", e: "ew", w: "ew", nw: "nwse", se: "nwse", ne: "nesw", sw: "nesw" };
    return `${map[k]}-resize`;
  };

  return (
    <g pointerEvents="auto" data-testid="image-crop-overlay">
      {/* Semi-transparent full-source image reference. The main <image> element
          only shows the cropped region (clipPath always applied), so we render
          a dimmed copy of the full original here to let the user see what they
          are cropping against. pointerEvents="none" so it never intercepts
          drags — the crop-rect below handles all interaction. */}
      <image
        href={el.attrs.href}
        x={ix}
        y={iy}
        width={iw}
        height={ih}
        preserveAspectRatio={el.attrs.preserveAspectRatio || "none"}
        opacity={0.35}
        style={{ pointerEvents: "none" }}
        data-testid="crop-reference-image"
      />

      {/* Darkened mask outside the crop rect (within the image bounds). */}
      {maskRects.map((r, i) => (
        <rect
          key={i}
          x={r.x}
          y={r.y}
          width={r.w}
          height={r.h}
          fill="rgba(0,0,0,0.5)"
          style={{ pointerEvents: "none" }}
        />
      ))}

      {/* Crop rect outline. fill="transparent" (not "none") so the whole rect
          area receives pointer events — dragging inside the box moves it,
          not just dragging the stroke. */}
      <rect
        x={rx}
        y={ry}
        width={rw}
        height={rh}
        fill="transparent"
        stroke="#FF6B00"
        strokeWidth={stroke}
        style={{ cursor: "move" }}
        onPointerDown={(e) => startDrag(e, "move")}
        data-testid="crop-rect"
      />

      {/* Resize handles. */}
      {HANDLES.map((h) => {
        const hx = rx + rw * h.x - handleSize / 2;
        const hy = ry + rh * h.y - handleSize / 2;
        return (
          <rect
            key={h.k}
            x={hx}
            y={hy}
            width={handleSize}
            height={handleSize}
            fill="#FF6B00"
            stroke="#ffffff"
            strokeWidth={stroke}
            style={{ cursor: cursorOf(h.k) }}
            onPointerDown={(e) => startDrag(e, "resize", h.k)}
            data-testid={`crop-handle-${h.k}`}
          />
        );
      })}
    </g>
  );
}
